import { useState, useEffect } from 'react';
import { useDevDateTime } from '@/hooks/useDevDateTime';
import { Clock, Calendar, AlertTriangle, RotateCcw } from 'lucide-react';

export function DevTimeTab() {
  const {
    now,
    isOverrideEnabled,
    overrideDate,
    setOverride,
    clearOverride,
    isDevMode,
  } = useDevDateTime();

  const [realTime, setRealTime] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(
    overrideDate?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
  );
  const [selectedTime, setSelectedTime] = useState(
    overrideDate?.toTimeString().slice(0, 5) || new Date().toTimeString().slice(0, 5)
  );

  // Update real time display every second
  useEffect(() => {
    const interval = setInterval(() => {
      setRealTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Update selected date/time when override changes
  useEffect(() => {
    if (overrideDate) {
      setSelectedDate(overrideDate.toISOString().split('T')[0]);
      setSelectedTime(overrideDate.toTimeString().slice(0, 5));
    }
  }, [overrideDate]);

  if (!isDevMode) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-800">DateTime override is only available in development mode.</p>
      </div>
    );
  }

  const handleApplyOverride = () => {
    const dateTime = new Date(`${selectedDate}T${selectedTime}`);
    setOverride(dateTime);
  };

  const handleQuickOffset = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    setOverride(date);
  };

  return (
    <div className="space-y-6">
      {/* Warning when override is active */}
      {isOverrideEnabled && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-orange-800">DateTime Override Active</h4>
            <p className="text-sm text-orange-700 mt-1">
              The app is using a custom date/time instead of the real system time.
              All date-based features will use this override.
            </p>
          </div>
        </div>
      )}

      {/* Time Display Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">Real System Time</span>
          </div>
          <div className="text-xl font-mono">{realTime.toLocaleString()}</div>
        </div>

        <div className={`p-4 rounded-lg border ${
          isOverrideEnabled
            ? 'bg-blue-50 border-blue-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Calendar className={`h-4 w-4 ${isOverrideEnabled ? 'text-blue-600' : 'text-gray-600'}`} />
            <span className={`text-sm font-medium ${isOverrideEnabled ? 'text-blue-800' : 'text-gray-600'}`}>
              Effective Time (App Uses This)
            </span>
          </div>
          <div className={`text-xl font-mono ${isOverrideEnabled ? 'text-blue-900' : ''}`}>
            {now().toLocaleString()}
          </div>
        </div>
      </div>

      {/* Override Controls */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg">
        <h4 className="font-semibold text-gray-800 mb-4">Set Custom Date/Time</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={handleApplyOverride}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Apply Override
          </button>
          {isOverrideEnabled && (
            <button
              onClick={clearOverride}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Use Real Time
            </button>
          )}
        </div>

        {/* Quick Offset Buttons */}
        <div className="border-t pt-4">
          <h5 className="text-sm font-medium text-gray-700 mb-2">Quick Offsets</h5>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleQuickOffset(-7)}
              className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm transition-colors"
            >
              -1 Week
            </button>
            <button
              onClick={() => handleQuickOffset(-1)}
              className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm transition-colors"
            >
              Yesterday
            </button>
            <button
              onClick={() => handleQuickOffset(1)}
              className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm transition-colors"
            >
              Tomorrow
            </button>
            <button
              onClick={() => handleQuickOffset(7)}
              className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm transition-colors"
            >
              +1 Week
            </button>
            <button
              onClick={() => handleQuickOffset(-30)}
              className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm transition-colors"
            >
              -1 Month
            </button>
            <button
              onClick={() => handleQuickOffset(30)}
              className="px-3 py-1 bg-gray-100 rounded hover:bg-gray-200 text-sm transition-colors"
            >
              +1 Month
            </button>
          </div>
        </div>
      </div>

      {/* Usage Instructions */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">Usage Instructions</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>- Override persists across page reloads (stored in localStorage)</li>
          <li>- All date-based features will use the override time</li>
          <li>- "Today" highlighting in calendar will reflect the override date</li>
          <li>- Deadline/overdue calculations will use the override</li>
          <li>- Override is automatically disabled in production builds</li>
          <li>- Access via console: <code className="bg-blue-100 px-1 rounded">window.devDateTime</code></li>
        </ul>
      </div>
    </div>
  );
}
